import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "./googleMapsLoader";
import { blockPathForPixel } from "./worldSoldPixels";
import { pixelIntegerFromLatLng } from "./pixel";
import { socketClient } from "../api/socket";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import { rememberMapPosition } from "./worldSearch";

export function StreetMap({ center, range, onReady, onSelect, selectedPixel }: {
  center: { lat: number; lng: number };
  range: number;
  onReady: (map: google.maps.Map | null) => void;
  onSelect: (point: { lat: number; lng: number }) => void;
  selectedPixel: number | null;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [failed, setFailed] = useState(false);
  const { items } = useCart();
  const { t } = useLocale();
  const initial = useRef({ center, range });
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const highlightRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    let disposed = false;
    let instance: google.maps.Map | null = null;
    let highlight: google.maps.Polygon | null = null;
    let picked: number | null = null;
    let off = () => {};
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sold: google.maps.Polygon[] = [];
    const listeners: google.maps.MapsEventListener[] = [];
    void (async () => {
      try {
        await loadGoogleMaps();
        const { Map, Polygon } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
        if (disposed || !host.current) return;
        const latitude = initial.current.center.lat;
        const metersPerPixel = Math.max(1, initial.current.range / Math.max(1, host.current.clientHeight));
        const zoom = Math.max(2, Math.min(20, Math.log2(156543.03392 * Math.cos(latitude * Math.PI / 180) / metersPerPixel)));
        instance = new Map(host.current, {
          center: initial.current.center, zoom, mapTypeId: "roadmap",
          mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
          clickableIcons: false, gestureHandling: "cooperative",
        });
        highlight = new Polygon({ map: instance, clickable: false, strokeColor: "#990033", strokeWeight: 3, fillColor: "#990033", fillOpacity: .15, zIndex: 10 });
        highlightRef.current = highlight;
        const show = (pixel: number) => highlight?.setPath(blockPathForPixel(pixel).map(({lat,lng}) => ({lat,lng})));
        listeners.push(instance.addListener("mousemove", (event: google.maps.MapMouseEvent) => {
          if (event.latLng && !window.matchMedia("(max-width: 768px)").matches) show(pixelIntegerFromLatLng(event.latLng.lat(), event.latLng.lng()));
        }));
        listeners.push(instance.addListener("click", (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) return;
          const point = event.latLng.toJSON();
          const pixel = pixelIntegerFromLatLng(point.lat, point.lng);
          show(pixel);
          if (window.matchMedia("(max-width: 768px)").matches && picked !== pixel) { picked = pixel; return; }
          picked = null;
          selectRef.current(point);
        }));
        const sync = () => {
          if (!instance || disposed) return;
          const bounds = instance.getBounds();
          const c = instance.getCenter();
          if (!bounds || !c) return;
          const ne = bounds.getNorthEast(), sw = bounds.getSouthWest();
          rememberMapPosition(c.lat(), c.lng());
          socketClient.send("sync", {
            minLat: sw.lat(), maxLat: ne.lat(), minLng: sw.lng(), maxLng: ne.lng(),
            range: Math.max(1, (ne.lat() - sw.lat()) * 111000 / 2), max_pixels: 2000,
          });
        };
        listeners.push(instance.addListener("idle", () => { clearTimeout(timer); timer = setTimeout(sync, 150); }));
        off = socketClient.on("pixels_all", message => {
          if (!Array.isArray(message.content) || disposed) return;
          sold.forEach(polygon => polygon.setMap(null));
          sold = [];
          for (const entry of message.content.slice(0, 2000)) {
            const row = typeof entry === "number" ? { pixel: entry } : entry;
            if (!row || !Number.isSafeInteger(Number(row.pixel))) continue;
            sold.push(new Polygon({
              map: instance, paths: blockPathForPixel(Number(row.pixel), Math.max(1, Number(row.size) || 1)).map(({lat,lng}) => ({lat,lng})),
              clickable: false, strokeColor: row.color || "#990033", strokeWeight: 1,
              fillColor: row.color || "#990033", fillOpacity: .4,
            }));
          }
        });
        setMap(instance);
        onReady(instance);
        await socketClient.connect();
        sync();
      } catch { if (!disposed) setFailed(true); }
    })();
    return () => {
      disposed = true;
      clearTimeout(timer);
      off();
      listeners.forEach(listener => listener.remove());
      sold.forEach(polygon => polygon.setMap(null));
      highlight?.setMap(null);
      highlightRef.current = null;
      onReady(null);
    };
  }, [onReady]);

  useEffect(() => {
    if (!map) return;
    const polygons = Object.values(items).map(pixel => new google.maps.Polygon({
      map, paths: blockPathForPixel(pixel.pixel).map(({lat,lng}) => ({lat,lng})),
      clickable: false, strokeColor: "#000000", strokeWeight: 2, fillColor: "#cccccc", fillOpacity: .5, zIndex: 5,
    }));
    return () => polygons.forEach(polygon => polygon.setMap(null));
  }, [map, items]);

  useEffect(() => {
    if (selectedPixel && highlightRef.current) highlightRef.current.setPath(blockPathForPixel(selectedPixel).map(({lat,lng}) => ({lat,lng})));
  }, [selectedPixel, map]);

  return <div className="street-map-container">
    <div ref={host} style={{ width: "100%", height: "100%" }} />
    {failed && <p role="alert" className="street-map-error">{t("world.streetMapFailed")}</p>}
  </div>;
}
